import { accountEntityName } from '../../../../../backend/features/accounts/shared';
import { useContactManagementTranslation } from '../../../../../ui/hooks/useContactManagementTranslation';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog, } from '@juvare/common-form-ui/WidgetEditDialog';
import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '@juvare/common-form-ui/WidgetForm/Utils';
import { useDebounce } from '@juvare/common-web-ui/hooks';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, FormControl, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import React from 'react';
import { accountSearchType } from '../types';
const debounceDelay = 800;
const minSearchInput = 3;
function getInternalData(props) {
    var _a;
    return (_a = props.internalData.value) !== null && _a !== void 0 ? _a : {};
}
function setInternalData(props, data) {
    props.internalData.value = data;
}
function isSearchValid(search) {
    return !!search && search.length >= minSearchInput;
}
const WidgetAccountSearch = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const accountId = props.getDataStringOrUndefined();
    const { search } = getInternalData(props);
    const error = getVisibleError(props);
    const tableView = ((_a = props.parentProps) === null || _a === void 0 ? void 0 : _a.config.type) === 'form.dataGrid';
    const disabled = props.config.disabled;
    const { t } = useContactManagementTranslation();
    const debouncedSearch = useDebounce(search, debounceDelay);
    const filter = [];
    // MUI autocomplete component expects selected value to exist in available options,
    // so we want to include it in the query if account is selected
    if (accountId) {
        filter.push({ id: accountId, op: '=' });
    }
    if (!accountId && isSearchValid(debouncedSearch)) {
        filter.push({
            column: 'search',
            op: 'full-text-query',
            value: debouncedSearch,
        }, ...((_b = props.config.accountFilters) !== null && _b !== void 0 ? _b : []));
    }
    const accounts = useStorageList(accountEntityName, filter.length
        ? {
            filter,
        }
        : undefined, undefined, undefined, {
        revalidateOnFocus: false,
    });
    const renderOptions = (renderProps, option) => (React.createElement(Box, Object.assign({}, renderProps, { key: option.id, sx: {
            display: 'flex',
            flexGrow: 1,
        } }),
        React.createElement(Typography, { variant: 'body1', sx: { alignSelf: 'flex-start', width: '50%', py: 1.25 } }, option.columns.displayName),
        React.createElement(Typography, { variant: 'body1', color: grey['700'], sx: { alignSelf: 'flex-start', width: '50%', py: 1.25, wordBreak: 'break-word' } }, option.columns.email)));
    const value = React.useMemo(() => { var _a, _b; return (_b = (_a = accounts.data) === null || _a === void 0 ? void 0 : _a.rows.find(({ id }) => id === accountId)) !== null && _b !== void 0 ? _b : null; }, [accountId, (_c = accounts.data) === null || _c === void 0 ? void 0 : _c.rows]);
    return (React.createElement(Box, null,
        React.createElement(Autocomplete, { id: props.id, fullWidth: true, size: tableView ? 'small' : 'medium', value: value, options: (_e = (_d = accounts.data) === null || _d === void 0 ? void 0 : _d.rows) !== null && _e !== void 0 ? _e : [], open: !accountId && isSearchValid(search) && isSearchValid(debouncedSearch), disabled: disabled, openOnFocus: true, loading: accounts.isLoading, filterOptions: (options) => options, renderOption: renderOptions, getOptionLabel: (option) => option.columns.displayName, forcePopupIcon: false, noOptionsText: React.createElement(Typography, { color: grey['700'], variant: 'body1', sx: { py: 1.25 } }, t('forms.accountSearchWidget.noUsersFound')), inputValue: (_f = search !== null && search !== void 0 ? search : value === null || value === void 0 ? void 0 : value.columns.displayName) !== null && _f !== void 0 ? _f : '', renderInput: (params) => {
                var _a, _b, _c;
                return (React.createElement(FormControl, { fullWidth: params.fullWidth, variant: 'outlined', size: params.size, required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error, disabled: params.disabled, sx: {
                        '& label.MuiInputLabel-root': { paddingRight: 3 },
                    } },
                    !tableView && (React.createElement(InputLabel, { htmlFor: props.id, variant: 'outlined', required: (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required, error: !!error, disabled: disabled }, props.config.label)),
                    React.createElement(OutlinedInput, Object.assign({}, params.InputProps, { inputProps: props.editMode ? Object.assign(Object.assign({}, params.inputProps), { tabIndex: -1 }) : params.inputProps, startAdornment: React.createElement(SearchIcon, { fontSize: 'medium' }), id: params.id, fullWidth: params.fullWidth, size: params.size, required: (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.required, error: !!error, label: tableView ? '' : props.config.label, disabled: params.disabled })),
                    React.createElement(FormHelperErrorDescription, { error: error, description: props.config.description })));
            }, onChange: (event, option) => {
                props.setDataStringOrUndefined(option === null || option === void 0 ? void 0 : option.id);
                setInternalData(props, { search: option === null || option === void 0 ? void 0 : option.columns.displayName });
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
            }, onInputChange: (event, value, reason) => {
                switch (reason) {
                    case 'input':
                        setInternalData(props, { search: value });
                        props.setDataStringOrUndefined(undefined);
                        renderForm(props, { autoSaveDebounce: props.config.autoSave });
                        break;
                    case 'clear':
                        setInternalData(props, {});
                        props.setDataStringOrUndefined(undefined);
                        renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                        break;
                }
            } })));
};
const WidgetAccountSearchEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: 'Account Search: ' + props.config.label,
        }, buildGeneralTab(props, {
            key: true,
            label: true,
            description: true,
            disabled: true,
            readOnly: true,
        }), buildValidateTab(props, { required: true, uniqueInList: true }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetAccountSearch, Object.assign({}, props))));
};
export const widgetAccountSearch = {
    type: accountSearchType,
    validate: (props) => {
        var _a, _b, _c, _d;
        if (isSkipValidate(props)) {
            return undefined;
        }
        const value = props.getData();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (value == null) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        if ((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.uniqueInList) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_d = props.config.validate.uniqueInListMessage) !== null && _d !== void 0 ? _d : error;
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a, _b;
        const data = props.getData();
        if (data) {
            const configValues = (_a = props.internalData.values) !== null && _a !== void 0 ? _a : [];
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), (_b = configValues.find((configValue) => configValue.value === data)) === null || _b === void 0 ? void 0 : _b.label);
        }
        return [];
    },
    component: WidgetAccountSearch,
    icon: React.createElement(FormatListBulletedIcon, { fontSize: 'small' }),
    editorComponent: WidgetAccountSearchEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, {
        type: accountSearchType,
        key: 'accountSearch',
        label: 'Account Search',
        values: [],
    }),
};
